"use client";
import { useEffect, useRef, useState } from "react";
import { ActionIcon, Box, Group, Paper, Portal, Text } from "@mantine/core";
import {
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
  IconPlayerStopFilled,
} from "@tabler/icons-react";

import {
  useTimeClock,
  getWorkedMs,
} from "@/app/_providers/TimeClockProvider/TimeClockProvider";
import LogTimeModal from "./LogTimeModal";
import styles from "./TimeClockWidget.module.scss";

// Where the pill was last dragged to; survives refresh so it stays out of the
// way once the user parks it somewhere.
const POSITION_KEY = "timeclock:widgetPosition";

// Default: bottom-LEFT, clear of the Alert toast and the POS quick-charge
// button which both live bottom-right.
const defaultPosition = () => ({ x: 20, y: window.innerHeight - 68 });

const clampToViewport = (pos: { x: number; y: number }) => ({
  x: Math.min(Math.max(pos.x, 8), window.innerWidth - 140),
  y: Math.min(Math.max(pos.y, 8), window.innerHeight - 48),
});

// Live h:mm:ss readout so the pill visibly ticks.
const formatClock = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${hours}:${pad(minutes)}:${pad(seconds)}`;
};

// Floating pill shown app-wide while the current team member has a timer
// running (the timer itself lives in Firestore, so it survives navigation,
// refresh, and closed tabs). Tap opens the stop/log modal; drag moves it
// (same <5px-is-a-click mechanic as POSTrigger).
const TimeClockWidget = () => {
  const {
    myTimer,
    isLogModalOpen,
    openLogModal,
    closeLogModal,
    pause,
    resume,
  } = useTimeClock();
  const [now, setNow] = useState(() => Date.now());

  const [position, setPosition] = useState<{ x: number; y: number } | null>(
    null
  );
  const positionRef = useRef<{ x: number; y: number } | null>(null);
  positionRef.current = position;
  const dragRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const hasMovedRef = useRef(false);
  const offsetRef = useRef({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  // Restore the saved spot (clamped in case the window is smaller now).
  useEffect(() => {
    let restored = defaultPosition();
    try {
      const raw = localStorage.getItem(POSITION_KEY);
      if (raw) restored = JSON.parse(raw);
    } catch {
      // ignore malformed storage
    }
    setPosition(clampToViewport(restored));
  }, []);

  // Keep it on-screen when the window shrinks.
  useEffect(() => {
    const onResize = () =>
      setPosition((pos) => (pos ? clampToViewport(pos) : pos));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!myTimer) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [myTimer]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      hasMovedRef.current = true;
      setPosition({
        x: e.clientX - offsetRef.current.x,
        y: e.clientY - offsetRef.current.y,
      });
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDraggingRef.current) return;
      hasMovedRef.current = true;
      const touch = e.touches[0];
      setPosition({
        x: touch.clientX - offsetRef.current.x,
        y: touch.clientY - offsetRef.current.y,
      });
    };

    const endDrag = () => {
      if (isDraggingRef.current && positionRef.current) {
        try {
          localStorage.setItem(
            POSITION_KEY,
            JSON.stringify(positionRef.current)
          );
        } catch {
          // ignore storage failures
        }
      }
      setIsDragging(false);
      // Deferred so the click handler still sees hasMovedRef and can swallow
      // the click that ends a drag.
      setTimeout(() => {
        isDraggingRef.current = false;
        hasMovedRef.current = false;
      }, 0);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", endDrag);
    document.addEventListener("touchmove", handleTouchMove);
    document.addEventListener("touchend", endDrag);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", endDrag);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", endDrag);
    };
  }, []);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = dragRef.current?.getBoundingClientRect();
    if (rect) {
      offsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }

    const startX = e.clientX;
    const startY = e.clientY;
    const moveThreshold = 5;

    const checkForDrag = (moveEvent: MouseEvent) => {
      const deltaX = Math.abs(moveEvent.clientX - startX);
      const deltaY = Math.abs(moveEvent.clientY - startY);
      if (deltaX > moveThreshold || deltaY > moveThreshold) {
        isDraggingRef.current = true;
        setIsDragging(true);
        document.removeEventListener("mousemove", checkForDrag);
      }
    };

    document.addEventListener("mousemove", checkForDrag);
    const cleanup = () => {
      document.removeEventListener("mousemove", checkForDrag);
      document.removeEventListener("mouseup", cleanup);
    };
    document.addEventListener("mouseup", cleanup);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const rect = dragRef.current?.getBoundingClientRect();
    const touch = e.touches[0];
    if (rect) {
      offsetRef.current = {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      };
    }

    const startX = touch.clientX;
    const startY = touch.clientY;
    const moveThreshold = 5;

    const checkForDrag = (moveEvent: TouchEvent) => {
      const moveTouch = moveEvent.touches[0];
      const deltaX = Math.abs(moveTouch.clientX - startX);
      const deltaY = Math.abs(moveTouch.clientY - startY);
      if (deltaX > moveThreshold || deltaY > moveThreshold) {
        isDraggingRef.current = true;
        setIsDragging(true);
        document.removeEventListener("touchmove", checkForDrag);
      }
    };

    document.addEventListener("touchmove", checkForDrag);
    const cleanup = () => {
      document.removeEventListener("touchmove", checkForDrag);
      document.removeEventListener("touchend", cleanup);
    };
    document.addEventListener("touchend", cleanup);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (hasMovedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    openLogModal();
  };

  if (!myTimer) return null;

  const isPaused = !!myTimer.pausedAt;
  // Worked time only, pauses don't tick.
  const elapsedMs = getWorkedMs(myTimer, now);

  const handlePauseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasMovedRef.current) return;
    (isPaused ? resume() : pause()).catch(() => {});
  };

  return (
    <>
      {position && (
        <Portal>
          <div
            ref={dragRef}
            className={styles.pill}
            style={{
              left: position.x,
              top: position.y,
              cursor: isDragging ? "grabbing" : "grab",
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          >
            <div
              style={{
                position: "relative",
                width: "fit-content",
                height: "fit-content",
              }}
            >
              {isDragging && <div className={styles.dragRing} />}
              <Paper
                className={`${styles.pillBody} ${
                  isPaused ? styles.pillBodyPaused : ""
                }`}
                shadow="md"
                radius="xl"
                px="md"
                py={8}
                onClick={handleClick}
                style={{ cursor: isDragging ? "grabbing" : "pointer" }}
              >
                <Group gap="xs" wrap="nowrap">
                  <Box
                    className={`${styles.dot} ${
                      isPaused ? styles.dotPaused : ""
                    }`}
                  />
                  <Text size="sm" fw={600} ff="monospace" c="white">
                    {formatClock(elapsedMs)}
                  </Text>
                  <Text size="sm" c="rgba(255, 255, 255, 0.75)">
                    {isPaused ? "Paused · " : ""}
                    Job #{myTimer.jobId}
                  </Text>
                  <ActionIcon
                    variant="transparent"
                    size="sm"
                    aria-label={isPaused ? "Resume timer" : "Pause timer"}
                    onClick={handlePauseClick}
                  >
                    {isPaused ? (
                      <IconPlayerPlayFilled size={16} color="white" />
                    ) : (
                      <IconPlayerPauseFilled size={16} color="white" />
                    )}
                  </ActionIcon>
                  <IconPlayerStopFilled size={16} color="white" />
                </Group>
              </Paper>
            </div>
          </div>
        </Portal>
      )}

      {isLogModalOpen && (
        <LogTimeModal
          opened={isLogModalOpen}
          onClose={closeLogModal}
          mode="log"
          jobUid={myTimer.jobUid}
          jobId={myTimer.jobId}
        />
      )}
    </>
  );
};

export default TimeClockWidget;
